import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import "./CreateLoadingPlanPage.scss";
import { Step1 } from "./steps/Step1";
import { Step2, Step2Ref } from "./steps/Step2";
import { Step3 } from "./steps/Step3";
import { Step4 } from "./steps/Step4";
import { Step5 } from "./steps/Step5";
import { ViewerProvider } from "../../contexts/ViewerContext";
import { StepFooter } from "./components/StepFooter";
import { useLoadingPlan } from "../../contexts/LoadingPlanContext";
import { loadingPlansService, partsService } from "../../services";
import { ROUTES } from "../../consts/routes";
import { LoadingPlanStatus } from "../../types/loadingPlan";
import { flattenPlacableNestedParts } from "../../components/viewer/ViewerComponent/partialRenest.utils";

export enum LoadingPlanSteps {
  STEP1 = 1,
  STEP2 = 2,
  STEP3 = 3,
  STEP4 = 4,
  STEP5 = 5,
}

const CreateLoadingPlanPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(LoadingPlanSteps.STEP1);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [showStepFooter, setShowStepFooter] = useState(false);

  // Step3 specific state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [nestingError, setNestingError] = useState<string | null>(null);
  const [nestingSuccess, setNestingSuccess] = useState<string | null>(null);

  // Step 4 "Finish Layout" in-progress state.
  const [isFinishingLayout, setIsFinishingLayout] = useState(false);
  // True when ViewerComponent background recalculation is in flight.
  const [isViewerRecalculating, setIsViewerRecalculating] = useState(false);

  const step2Ref = useRef<Step2Ref>(null);
  const nestingPollRef = useRef<{ interval?: NodeJS.Timeout; timeout?: NodeJS.Timeout }>({});

  const { updateCurrentLoadingPlan, currentLoadingPlan, clearCurrentLoadingPlan } = useLoadingPlan();
  const navigate = useNavigate();
  const { loadingPlanId } = useParams<{ loadingPlanId: string }>();

  const stopNestingPolling = useCallback(() => {
    clearInterval(nestingPollRef.current.interval);
    clearTimeout(nestingPollRef.current.timeout);
    nestingPollRef.current = {};
  }, []);

  // Poll a full nesting run until the plan settles on ready/failed.
  const pollNestingStatus = useCallback((planId: number) => {
    stopNestingPolling();
    setIsGenerating(true);

    const interval = setInterval(async () => {
      try {
        const updatedPlan = await loadingPlansService.fetchLoadingPlanById(planId);

        if (updatedPlan.status === LoadingPlanStatus.READY) {
          stopNestingPolling();
          setIsGenerating(false);
          setIsGenerated(true);
          setNestingSuccess('Nesting plan generated successfully. You can now review and publish.');
          updateCurrentLoadingPlan({ ...updatedPlan });
        }
        if (updatedPlan.status === LoadingPlanStatus.FAILED) {
          stopNestingPolling();
          setIsGenerating(false);
          setNestingError('Nesting generation failed. Please check your configuration and try again.');
          updateCurrentLoadingPlan({ ...updatedPlan });
        }
      } catch (error) {
        console.error('Failed to fetch loading plan status:', error);
        stopNestingPolling();
        setIsGenerating(false);
      }
    }, Number(process.env.REACT_APP_NESTING_POLLING_INTERVAL || 5000)); // Poll interval from env variable, default to 5000ms

    const timeout = setTimeout(() => {
      stopNestingPolling();
      setIsGenerating((current) => {
        if (current) {
          console.error('Nesting generation timeout');
        }
        return false;
      });
    }, Number(process.env.REACT_APP_NESTING_POLLING_TIMEOUT || 60) * 60 * 1000); // Timeout from env variable, default to 60 minutes

    nestingPollRef.current = { interval, timeout };
  }, [stopNestingPolling, updateCurrentLoadingPlan]);

  // Load loading plan by ID if present in URL
  useEffect(() => {
    const loadPlan = async () => {
      if (loadingPlanId && !currentLoadingPlan.id) {
        try {
          const plan = await loadingPlansService.fetchLoadingPlanById(parseInt(loadingPlanId));
          const partsResponse = await partsService.fetchLoadingPlanViewParts(Number(loadingPlanId));

          updateCurrentLoadingPlan({ ...plan, includedParts: partsResponse.parts });
          setCurrentPlanId(loadingPlanId);

          if (plan.status === LoadingPlanStatus.READY) {
            setCurrentStep(LoadingPlanSteps.STEP4);
          }

          // IN_PROGRESS is either IFC extraction (no parts included yet; Step1 resumes its own
          // polling) or a full nesting run (parts included) - resume the latter on Step 3.
          if (plan.status === LoadingPlanStatus.IN_PROGRESS && partsResponse.parts.length > 0) {
            setCurrentStep(LoadingPlanSteps.STEP3);
            pollNestingStatus(plan.id);
          }

          setShowStepFooter(true);
        } catch (error) {
          console.error('Failed to load loading plan:', error);
        }
      }
    };
    loadPlan();
  }, [loadingPlanId, currentLoadingPlan.id, updateCurrentLoadingPlan, pollNestingStatus]);

  // Clear current loading plan when component unmounts (page close or redirect)
  useEffect(() => {
    return () => {
      clearCurrentLoadingPlan();
      clearInterval(nestingPollRef.current.interval);
      clearTimeout(nestingPollRef.current.timeout);
    };
  }, []);

  // Step navigation handlers
  const handlePrevStep = () => {
    if (currentStep > LoadingPlanSteps.STEP1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePublishPlan = useCallback(async () => {
    // Update the current loading plan status to published
    if (currentLoadingPlan && currentLoadingPlan.id) {
      try {
        const updatedPlan = await loadingPlansService.updateLoadingPlan(currentLoadingPlan.id, {
          status: LoadingPlanStatus.PUBLISHED
        });
        updateCurrentLoadingPlan({ ...updatedPlan });
        setCurrentPlanId(updatedPlan.id.toString());
        setCurrentStep(LoadingPlanSteps.STEP5);
      } catch (error) {
        console.error('Failed to publish loading plan:', error);
      }
    }
  }, [currentLoadingPlan, updateCurrentLoadingPlan]);

  // Nest remaining unplaced parts to complete layout before publishing.
  const handleFinishLayout = useCallback(async () => {
    if (!currentLoadingPlan?.id || !currentLoadingPlan.nestingOutput || isViewerRecalculating) return;

    const rawParts = currentLoadingPlan.nestingOutput.nested_parts || [];
    const preplacedParts = flattenPlacableNestedParts(rawParts).map((part: any) => ({
      part_id: part.part_id,
      order_number: part.order_number,
      assigned_loading_layer: part.assigned_loading_layer,
      // Forward orientation_index for preplaced parts.
      orientation_index: part.orientation_index,
      transform_matrix: part.transform_matrix,
    }));

    setIsFinishingLayout(true);
    setNestingError(null);

    try {
      await loadingPlansService.requestNesting({
        loadingPlanId: currentLoadingPlan.id,
        nested_parts: preplacedParts,
      });

      const pollIntervalMs = Number(process.env.REACT_APP_NESTING_POLLING_INTERVAL || 5000);
      const timeoutMs = Number(process.env.REACT_APP_NESTING_POLLING_TIMEOUT || 60) * 60 * 1000;

      const pollInterval = setInterval(async () => {
        try {
          const updatedPlan = await loadingPlansService.fetchLoadingPlanById(currentLoadingPlan.id);

          if (updatedPlan.status === LoadingPlanStatus.READY) {
            clearInterval(pollInterval);
            setIsFinishingLayout(false);
            updateCurrentLoadingPlan({ ...updatedPlan });
          } else if (updatedPlan.status === LoadingPlanStatus.FAILED) {
            clearInterval(pollInterval);
            setIsFinishingLayout(false);
            setNestingError('Finishing the layout failed. Please try again.');
            updateCurrentLoadingPlan({ ...updatedPlan });
          }
        } catch (error) {
          console.error('Failed to fetch loading plan status:', error);
          clearInterval(pollInterval);
          setIsFinishingLayout(false);
        }
      }, pollIntervalMs);

      setTimeout(() => {
        clearInterval(pollInterval);
        setIsFinishingLayout((current) => {
          if (current) {
            console.error('Finish layout timeout');
          }
          return false;
        });
      }, timeoutMs);
    } catch (error) {
      console.error('Failed to finish loading plan layout:', error);
      setIsFinishingLayout(false);
    }
  }, [currentLoadingPlan, isViewerRecalculating, updateCurrentLoadingPlan]);

  const handleNextStep = useCallback(async () => {
    // Step 1: Create new loading plan before moving to Step 2
    if (currentStep === LoadingPlanSteps.STEP1) {
      try {
        const updatedPlan = await loadingPlansService.updateLoadingPlan(currentLoadingPlan.id, {
          title: currentLoadingPlan.title,
          shipDate: currentLoadingPlan.shipDate
        });
        updateCurrentLoadingPlan({ ...updatedPlan });
        setCurrentStep(LoadingPlanSteps.STEP2);
      } catch (error) {
        console.error('Failed to update loading plan:', error);
        // Optionally show error to user
      }
      return;
    }

    if (currentStep === LoadingPlanSteps.STEP2) {
      if (step2Ref.current && !step2Ref.current.validate()) {
        return;
      }
      try {
        const updatedPlan = await loadingPlansService.updateLoadingPlan(currentLoadingPlan.id, {
          trailerLength: currentLoadingPlan.trailerLength,
          trailerMaxWidth: currentLoadingPlan.trailerMaxWidth,
          trailerMaxWeight: currentLoadingPlan.trailerMaxWeight,
          trailerMaxHeight: currentLoadingPlan.trailerMaxHeight,
        });
        updateCurrentLoadingPlan({ ...updatedPlan });
        setCurrentStep(LoadingPlanSteps.STEP3);
      } catch (error) {
        console.error('Failed to update loading plan:', error);
        // Optionally show error to user
      }
      return;
    }

    // Step 3: Handle generate/navigate logic
    if (currentStep === LoadingPlanSteps.STEP3) {
      const isPlanChanged = currentLoadingPlan.status !== LoadingPlanStatus.READY;

      const partsResponse = await partsService.fetchLoadingPlanViewParts(Number(currentLoadingPlan.id));

      updateCurrentLoadingPlan({ includedParts: partsResponse.parts });

      if (!isPlanChanged) {
        setCurrentStep(LoadingPlanSteps.STEP4);
        return;
      }

      setIsGenerating(true);
      setNestingError(null);
      setNestingSuccess(null);

      try {
        // Request nesting calculation
        await loadingPlansService.requestNesting({
          loadingPlanId: currentLoadingPlan.id
        });

        pollNestingStatus(currentLoadingPlan.id);
      } catch (error) {
        console.error('Failed to generate nesting plan:', error);
        setIsGenerating(false);
        // Optionally show error to user
      }
      return;
    }

    // Step 4: Finish layout if incomplete before publishing.
    if (currentStep === LoadingPlanSteps.STEP4) {
      if (isViewerRecalculating) return;

      if (!currentLoadingPlan.nestingCoverageComplete) {
        await handleFinishLayout();
        return;
      }

      handlePublishPlan();
      setCurrentStep(LoadingPlanSteps.STEP5);
      return;
    }

    // For other steps, just move to next
    if (currentStep < LoadingPlanSteps.STEP4) {
      setCurrentStep(currentStep + 1);
      setShowStepFooter(true); // Ensure footer stays visible when moving to next steps
    }
  }, [currentLoadingPlan, currentStep, handleFinishLayout, handlePublishPlan, isGenerated, isViewerRecalculating, pollNestingStatus, updateCurrentLoadingPlan]);

  // Step-specific handlers
  const handleDelete = async () => {
    clearCurrentLoadingPlan();
    await loadingPlansService.deleteLoadingPlan(currentLoadingPlan.id);
    navigate(ROUTES.home);
  };

  const handleBackToDashboard = () => {
    clearCurrentLoadingPlan();
    navigate(ROUTES.home);
  }

  const handleSaveDraft = async () => {
    if (currentStep === LoadingPlanSteps.STEP1) {
      if (!currentLoadingPlan.id) {
        const newPlan = await loadingPlansService.createLoadingPlan({
          title: currentLoadingPlan.title,
          shipDate: currentLoadingPlan.shipDate,
        });
        updateCurrentLoadingPlan({ ...newPlan });
        navigate(ROUTES.home);
        return;
      }

      const updatedPlan = await loadingPlansService.updateLoadingPlan(currentLoadingPlan.id, {
        partIds: currentLoadingPlan.includedParts.map(part => part.id),
      });
      updateCurrentLoadingPlan({ ...updatedPlan });
    }

    if (currentStep === LoadingPlanSteps.STEP2) {
      const updatedPlan = await loadingPlansService.updateLoadingPlan(currentLoadingPlan.id, {
        trailerLength: currentLoadingPlan.trailerLength,
        trailerMaxWidth: currentLoadingPlan.trailerMaxWidth,
        trailerMaxWeight: currentLoadingPlan.trailerMaxWeight,
        trailerMaxHeight: currentLoadingPlan.trailerMaxHeight,
      });
      updateCurrentLoadingPlan({ ...updatedPlan });
    }

    navigate(ROUTES.home);
  };

  return (
    <ViewerProvider>
      <Box className="create-loading-plan-page">
        {/* Back to Dashboard */}
        {currentStep !== LoadingPlanSteps.STEP5 && (
          <Box className="back-button-row" onClick={handleBackToDashboard} style={{ cursor: 'pointer' }}>
            <IconButton size="small" className="back-icon-button">
              <ArrowBackIcon color="primary" />
            </IconButton>
            <Typography variant="body2" color="primary" fontWeight={500}>
              BACK TO DASHBOARD
            </Typography>
          </Box>)}

        {/* Page Title */}
        {currentStep !== LoadingPlanSteps.STEP5 && (
          <Typography className="page-title">
            {loadingPlanId ? `Edit Loading Plan ${loadingPlanId}` : "Create New Loading Plan"}
          </Typography>)}

        {/* Step Indicator */}
        {currentStep !== LoadingPlanSteps.STEP5 && (
          <Box className="step-indicator">
            <Box className={`step-bar ${currentStep === LoadingPlanSteps.STEP1 ? 'active' : 'inactive'}`} />
            <Box className={`step-bar ${currentStep === LoadingPlanSteps.STEP2 ? 'active' : 'inactive'}`} />
            <Box className={`step-bar ${currentStep === LoadingPlanSteps.STEP3 ? 'active' : 'inactive'}`} />
            <Box className={`step-bar ${currentStep === LoadingPlanSteps.STEP4 ? 'active' : 'inactive'}`} />
          </Box>
        )}

        {/* Steps */}
        {nestingError && currentStep === LoadingPlanSteps.STEP3 && (
          <Alert severity="error" onClose={() => setNestingError(null)} sx={{ mb: 2 }}>
            {nestingError}
          </Alert>
        )}
        {nestingSuccess && currentStep === LoadingPlanSteps.STEP3 && (
          <Alert severity="success" onClose={() => setNestingSuccess(null)} sx={{ mb: 2 }}>
            {nestingSuccess}
          </Alert>
        )}
        {currentStep === LoadingPlanSteps.STEP1 && <Step1 setCurrentStep={setCurrentStep} setUploadedFile={setUploadedFile} setShowStepFooter={setShowStepFooter} />}
        {currentStep === LoadingPlanSteps.STEP2 && <Step2 ref={step2Ref} setCurrentStep={setCurrentStep} />}
        {currentStep === LoadingPlanSteps.STEP3 && (
          <Step3
            setCurrentStep={setCurrentStep}
            isGenerating={isGenerating}
            isGenerated={isGenerated}
          />
        )}
        {currentStep === LoadingPlanSteps.STEP4 && <Step4 setCurrentStep={setCurrentStep} uploadedFile={uploadedFile} currentPlanId={currentPlanId} setCurrentPlanId={setCurrentPlanId} onRecalculatingChange={setIsViewerRecalculating} />}
        {currentStep === LoadingPlanSteps.STEP5 && <Step5 setCurrentStep={setCurrentStep} currentPlanId={currentPlanId} />}

        {/* StepFooter - shown for steps 1-4 */}
        {currentStep !== LoadingPlanSteps.STEP5 && showStepFooter && (
          <StepFooter
            currentStep={currentStep}
            isGenerating={isGenerating}
            isGenerated={isGenerated}
            isFinishingLayout={isFinishingLayout}
            isViewerRecalculating={isViewerRecalculating}
            onDelete={handleDelete}
            onSaveDraft={handleSaveDraft}
            onPrev={handlePrevStep}
            onNext={handleNextStep}
          />
        )}
      </Box>
    </ViewerProvider>
  );
};

export { CreateLoadingPlanPage };